import {
  APIComponentAttributes,
  APIFieldType,
  PROVENANCE_FIELD_KEYS,
  ReportColumn,
  ReportColumnType,
  SortOrder,
} from '@ardoq/api-types';
import {
  ColDef,
  ColGroupDef,
  Column,
  ColumnState,
  SuppressHeaderKeyboardEventParams,
  SuppressKeyboardEventParams,
  EditableCallbackParams,
} from 'ag-grid-enterprise';
import { isColumnSortAble } from '@ardoq/report-reader';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { groupBy, partition, sortBy } from 'lodash';
import { createComponentsOperations } from '../createComponents/createComponentsOperations';
import { cellRendererPropsOperations } from '../CellRenderers/cellRendererPropsOperations';
import type { TooltipParams } from '../CellRenderers/types';
import {
  ColumnDefinitionArdoqMetadata,
  FieldGroupMembership,
  InventoryColumnDef,
  InventoryColumnDefinitionWithOptionList,
  InventoryColumnDefinitionWithSelectionState,
  InventoryContext,
  InventorySorting,
  ReferenceColumnDirection,
  RequestableColumnDefinition,
} from '../types';
import { TAGS_COLUMN } from '../inventoryConsts'; // TODO Unify with the other constant we've

const isDateTimeColumn = ({ dataType, isDateRangeColumn }: ReportColumn) =>
  dataType === APIFieldType.DATE_TIME && !isDateRangeColumn;

const isDateOnlyColumn = ({ dataType, isDateRangeColumn }: ReportColumn) =>
  dataType === APIFieldType.DATE_ONLY && !isDateRangeColumn;

const isDateColumn = (column: ReportColumn) =>
  isDateOnlyColumn(column) || isDateTimeColumn(column);

const isCheckBoxColumn = ({ dataType }: ReportColumn) =>
  dataType === APIFieldType.CHECKBOX;

const isDateRangeColumn = ({ isDateRangeColumn }: ReportColumn) =>
  isDateRangeColumn;

const isDateTimeRangeColumn = ({
  isDateRangeColumn,
  dataType,
}: ReportColumn): boolean =>
  (dataType === APIFieldType.DATE_TIME_RANGE && isDateRangeColumn) ?? false;

const isDateOnlyRangeColumn = ({
  isDateRangeColumn,
  dataType,
}: ReportColumn): boolean =>
  (dataType === APIFieldType.DATE_ONLY_RANGE && isDateRangeColumn) ?? false;

const isUserColumn = ({ dataType }: ReportColumn) =>
  dataType === APIFieldType.USER;

const isTextAreaColumn = ({ dataType }: ReportColumn) =>
  dataType === APIFieldType.TEXT_AREA;

const isNumberColumn = ({ dataType }: ReportColumn) =>
  dataType === APIFieldType.NUMBER;

const isNameColumn = ({ key }: ReportColumn) => key === 'name';

const isParentColumn = ({ key }: ReportColumn) => key === 'parent';

const isProvenanceColumn = ({ key }: ReportColumn): boolean =>
  PROVENANCE_FIELD_KEYS.some(provenanceKey => provenanceKey === key);

const isColumnSortable = (
  column: ReportColumn,
  isHierarchyEnabled: boolean
): boolean =>
  isColumnSortAble(column) &&
  // Sorting with provenance columns will be supported in the future
  !isProvenanceColumn(column) &&
  // Sorting by parent doesn't make sense in hierarchies; sort by name
  // already includes the parent and works better
  !(isParentColumn(column) && isHierarchyEnabled);

const isURLColumn = ({ dataType }: ReportColumn) =>
  dataType === APIFieldType.URL;

const isEmailColumn = ({ dataType }: ReportColumn) =>
  dataType === APIFieldType.EMAIL;

const isReferenceColumn = (
  column: ReportColumn
): column is ReportColumn & {
  type:
    | ReportColumnType.REFERENCE_TYPE_OUTGOING
    | ReportColumnType.REFERENCE_TYPE_INCOMING;
} =>
  column.type === ReportColumnType.REFERENCE_TYPE_INCOMING ||
  column.type === ReportColumnType.REFERENCE_TYPE_OUTGOING;

const isListColumn = ({ dataType }: ReportColumn) =>
  dataType === APIFieldType.LIST;

const isSelectMultipleListColumn = ({ dataType }: ReportColumn) =>
  dataType === APIFieldType.SELECT_MULTIPLE_LIST;

const isTypeColumn = ({ key }: ReportColumn) => key === 'type'; // Reports unify comps and refs to only have this column

const isFileColumn = ({ dataType }: ReportColumn) =>
  dataType === APIFieldType.FILE;

const isWorkspaceColumn = ({ key }: ReportColumn) => key === 'rootWorkspace';

const isTagsColumn = ({ key }: ReportColumn) => key === TAGS_COLUMN;

const isArdoqOID = ({ key }: ReportColumn) => key === '_id';

const isIdColumn = ({ key }: ReportColumn) => key === '_id';

const isComponentKeyColumn = ({ key }: ReportColumn) => key === 'component-key';

const isDescriptionColumn = ({ key }: ReportColumn) => key === 'description';

const isLucidchartColumn = ({ key }: ReportColumn) =>
  key === 'lucidchartDocuments';

const isCreatedAtColumn = ({ key }: ReportColumn) => key === 'created';

const isLastUpdatedDateColumn = ({ key }: ReportColumn) =>
  key === 'lastUpdated';

const getIsCellEditable = (
  params: EditableCallbackParams<APIComponentAttributes, any, InventoryContext>
) => {
  if (params.context.config.mode === 'filterData') return false;
  if (params.colDef.field === 'rootWorkspace') {
    return Boolean(
      params.data &&
      createComponentsOperations.isComponentNotPersisted(params.data._id)
    );
  }
  if (
    !cellRendererPropsOperations.hasEditPermissionForComponent(params) &&
    !canEditIncomingReferenceColumn(params)
  ) {
    return false;
  }
  if (
    cellRendererPropsOperations.requiresAnotherEntityChangeForUnpersistedComponent(
      params
    )
  ) {
    return false;
  }
  if (cellRendererPropsOperations.isLockedComponent(params)) return false;
  if (cellRendererPropsOperations.isReadOnlyField(params)) return false;
  if (!cellRendererPropsOperations.isComponentColumnApplicable(params)) {
    return false;
  }
  if (params.colDef.field === 'type' || params.colDef.field === 'parent') {
    return !cellRendererPropsOperations.isRigidModel(params);
  }

  if (cellRendererPropsOperations.isDerivedField(params)) {
    return false;
  }

  if (cellRendererPropsOperations.isCalculatedField(params)) {
    return false;
  }

  if (cellRendererPropsOperations.isConfidentialFieldUserCannotSee(params)) {
    return false;
  }

  if (cellRendererPropsOperations.isExternallyManagedFieldOnComponent(params)) {
    return false;
  }
  return true;
};

/**
 * The row's component is the target of an incoming reference, and a target only
 * needs view access.
 */
const canEditIncomingReferenceColumn = (
  params:
    | TooltipParams
    | EditableCallbackParams<APIComponentAttributes, any, InventoryContext>
): boolean =>
  isIncomingReferenceColumn(params.colDef) &&
  cellRendererPropsOperations.hasReadPermissionForComponent(
    params.data,
    params.context
  );

const isIncomingReferenceColumn = (
  colDef:
    | InventoryColumnDef
    | ColGroupDef<APIComponentAttributes>
    | null
    | undefined
): boolean => {
  if (!colDef || !('context' in colDef)) return false;

  const context = colDef.context as ColumnDefinitionArdoqMetadata;
  return (
    context.inventoryColumnType === 'reference' &&
    context.direction === 'incoming'
  );
};

const getDefaultSort = (): { colId: 'name'; sort: 'asc' } => ({
  colId: 'name',
  sort: 'asc',
});

const getColumnHeaderLabel = (column: ReportColumn): string => {
  if (!isReferenceColumn(column)) return column.label;
  if (column.type === ReportColumnType.REFERENCE_TYPE_INCOMING) {
    return `← ${column.label} ←`;
  }
  return `→ ${column.label} →`;
};

const columnIdIsForOutgoingReference = (columnId: string): boolean => {
  return columnId.endsWith('--outgoing');
};

export const ROOT_WORKSPACE_COLUMN: ReportColumn = {
  key: 'rootWorkspace',
  label: 'Workspace',
  type: ReportColumnType.FIELD,
};

export const columnsVisibleByDefault: readonly string[] = [
  'name',
  'description',
  'type',
  ROOT_WORKSPACE_COLUMN.key,
  'parent',
  'lastUpdated',
];

const isColumnVisibleByDefault = (key: string): boolean => {
  return columnsVisibleByDefault.includes(key);
};

const isNameAgGridColumn = (columnDef: ColDef) => {
  return columnDef.colId === 'name';
};

const isExtraAgGridColumn = (columnDef: ColDef): boolean =>
  Boolean(columnDef.context?.isExtraColumn);

const getResetPinned = (columnDef: ColDef): ColumnState['pinned'] => {
  if (isNameAgGridColumn(columnDef)) return 'left';
  if (isExtraAgGridColumn(columnDef)) return columnDef.pinned ?? null;
  return null;
};

const ORDER_OF_COLUMN_WITHOUT_FIELD = Number.MAX_SAFE_INTEGER;

type SortToDefaultOrderArgs<T> = {
  visibleKeys: readonly string[];
  fieldOrderByColumnKey: ReadonlyMap<string, number>;
  getKey: (column: T) => string;
  getLabel: (column: T) => string;
  getReferenceDirection: (column: T) => ReferenceColumnDirection | null;
};

/**
 * Generic sorting function that sorts columns to default order:
 * 1. Default visible columns in the order defined by `visibleKeys`
 * 2. The remaining component columns in field order, then alphabetically.
 *    Columns not backed by a field come last.
 * 3. Reference columns last, alphabetically, incoming before outgoing when a
 *    reference type contributes a column in both directions
 */
const sortToDefaultOrder = <T>(
  columns: T[],
  {
    visibleKeys,
    fieldOrderByColumnKey,
    getKey,
    getLabel,
    getReferenceDirection,
  }: SortToDefaultOrderArgs<T>
): T[] => {
  const [defaultColumns, nonDefaultColumns] = partition(columns, column =>
    visibleKeys.includes(getKey(column))
  );
  const [referenceColumns, componentColumns] = partition(
    nonDefaultColumns,
    column => getReferenceDirection(column) !== null
  );

  return [
    ...sortBy(defaultColumns, column => visibleKeys.indexOf(getKey(column))),
    ...sortBy(componentColumns, [
      column =>
        fieldOrderByColumnKey.get(getKey(column)) ??
        ORDER_OF_COLUMN_WITHOUT_FIELD,
      column => getLabel(column),
    ]),
    ...sortBy(referenceColumns, [
      column => getLabel(column),
      column => (getReferenceDirection(column) === 'incoming' ? 0 : 1),
    ]),
  ];
};

const getReferenceColumnDirection = (
  column: ReportColumn
): ReferenceColumnDirection | null => {
  if (column.type === ReportColumnType.REFERENCE_TYPE_INCOMING) {
    return 'incoming';
  }
  if (column.type === ReportColumnType.REFERENCE_TYPE_OUTGOING) {
    return 'outgoing';
  }
  return null;
};

const getAgGridReferenceColumnDirection = (
  columnDef: ColDef
): ReferenceColumnDirection | null => {
  const context = columnDef.context as
    | ColumnDefinitionArdoqMetadata
    | undefined;
  if (context?.inventoryColumnType !== 'reference') return null;
  return context.direction;
};

const getColumnId = (column: {
  key: string;
  fieldGroup?: FieldGroupMembership;
}): string =>
  column.fieldGroup
    ? `field-group:${column.fieldGroup.id}:${column.key}`
    : column.key;

const toLeafColumnDefs = <TData = APIComponentAttributes>(
  columnDefs: (ColDef<TData> | ColGroupDef<TData>)[]
): ColDef<TData>[] =>
  columnDefs.flatMap(columnDef =>
    'children' in columnDef ? toLeafColumnDefs(columnDef.children) : [columnDef]
  );

const hasDisplayedColumnInGroup = (displayedColumns: Column[]): boolean =>
  displayedColumns.some(column => column.getParent()?.isPadding() === false);

const getBackendFieldKey = <TData = APIComponentAttributes>(
  columnDef: ColDef<TData>
): string | undefined => columnDef.field;

const isRequestableColumnDef = (
  columnDef: ColDef<APIComponentAttributes>
): columnDef is RequestableColumnDefinition =>
  columnDef.field !== undefined &&
  columnDef.context !== undefined &&
  !columnDef.context.isExtraColumn;

const findColumnDefByColumnId = <TData = APIComponentAttributes>(
  columnDefs: ColDef<TData>[],
  colId: string
): ColDef<TData> | undefined =>
  columnDefs.find(columnDef => (columnDef.colId ?? columnDef.field) === colId);

const toSorting = (
  columnDefs: ColDef<APIComponentAttributes>[],
  colId: string,
  sortDirection: 'asc' | 'desc'
): InventorySorting => {
  const sortedColumnDef = findColumnDefByColumnId(columnDefs, colId);
  const sortBy = sortedColumnDef
    ? getBackendFieldKey(sortedColumnDef)
    : undefined;
  return {
    sortBy: sortBy ?? colId,
    order: sortDirection === 'asc' ? SortOrder.ASC : SortOrder.DESC,
  };
};

const getColumnIdsBackedByField = (
  columnDefs: ColDef<APIComponentAttributes>[],
  fieldKey: string
): string[] =>
  columnDefs
    .filter(columnDef => getBackendFieldKey(columnDef) === fieldKey)
    .map(columnDef => columnDef.colId ?? fieldKey);

const getDefaultColumnDefinitions = <TData>(
  columnDefs: ColDef<TData>[],
  defaultVisibleColumnKeys: readonly string[],
  fieldOrderByColumnKey: ReadonlyMap<string, number>
): ColumnState[] => {
  const resetColumnDefs = columnDefs.map((columnDef: ColDef) => {
    return {
      ...columnDef,
      pinned: getResetPinned(columnDef),
      hide: !defaultVisibleColumnKeys.includes(columnDef.colId!),
      sort: isNameAgGridColumn(columnDef) ? 'asc' : null,
      sortIndex: isNameAgGridColumn(columnDef) ? 0 : null,
      colId: columnDef.colId!,
    };
  }) satisfies ColumnState[];

  return sortToDefaultOrder(resetColumnDefs, {
    visibleKeys: defaultVisibleColumnKeys,
    fieldOrderByColumnKey,
    getKey: def => def.colId,
    getLabel: def => def.headerName ?? '',
    getReferenceDirection: getAgGridReferenceColumnDirection,
  });
};

const sortColumnsToDefaultOrder = (
  columns: InventoryColumnDefinitionWithOptionList[],
  fieldOrderByColumnKey: ReadonlyMap<string, number>
): InventoryColumnDefinitionWithOptionList[] =>
  sortToDefaultOrder(columns, {
    visibleKeys: columnsVisibleByDefault,
    fieldOrderByColumnKey,
    getKey: column => column.key,
    getLabel: column => column.label,
    getReferenceDirection: getReferenceColumnDirection,
  });

/**
 * A field name can occur once per model, so a multi-workspace scope can hold
 * several fields with the same name and different orders. The lowest order wins.
 */
const getFieldOrderByName = (
  scopeData: EnhancedScopeData
): ReadonlyMap<string, number> =>
  new Map(
    Object.entries(groupBy(scopeData.fields, field => field.name)).map(
      ([name, fields]): [string, number] => [
        name,
        Math.min(...fields.map(field => field._order)),
      ]
    )
  );

const shouldSuppressCellTooltip = (
  column: InventoryColumnDefinitionWithSelectionState
): boolean => !column.isExtraColumn && Boolean(column.suppressCellTooltip);

const canColumnUseFillHandle = (column: ReportColumn): boolean => {
  return (
    !cellRendererPropsOperations.isDefaultPropertyReadOnly(column.key) &&
    !isReferenceColumn(column)
  ); // TODO AM read only fields should not be editable and when I lack permissions
};

/**
 * Needed for date picker to work properly - allow manual date updates and saving them with enter key.
 * Only suppresses when editing so that Tab/Enter still work for cell navigation when not editing.
 */
const suppressEnterTabEscapeAndArrowKeys = (
  params: SuppressKeyboardEventParams
) => {
  // Only suppress keyboard events when the cell is in edit mode
  if (!params.editing) {
    return false;
  }

  const event = params.event;

  if (event.key === 'Enter' || event.key === 'Escape' || event.key === 'Tab') {
    return true; // suppress ag-Grid's default - date picker has its own handlers
  }

  return false;
};

/**
 * Combobox editors own Enter: it belongs to the open listbox, which picks,
 * creates or toggles the focused option. For the multi-value ones the editor
 * then commits the cell on the Enter that follows the menu closing — see
 * `useMultiValueComboboxKeyboard`.
 *
 * Tab and Escape are deliberately left to AG Grid, so they keep meaning what
 * they mean in every other cell: Tab commits and moves to the next cell,
 * Escape discards. Tab is the route out that always works — the menu of a
 * multi-value select never closes as you pick (`closeMenuOnSelect` is false),
 * so Enter can be busy toggling options indefinitely.
 */
const suppressEnterForComboboxEditors = (
  params: SuppressKeyboardEventParams
) => {
  // Only suppress keyboard events when the cell is in edit mode
  if (!params.editing) {
    return false;
  }
  const { key } = params.event;
  return key === 'Enter';
};

/**
 * The reference editor is the exception: it is a `role="dialog"` with its own
 * buttons and inputs, so Tab has to stay inside it rather than commit the cell
 * and move on. Escape still reaches the grid and discards the edit.
 */
const suppressEnterAndTabForDialogEditors = (
  params: SuppressKeyboardEventParams
) => {
  if (!params.editing) {
    return false;
  }

  const { key } = params.event;
  return key === 'Enter' || key === 'Tab';
};

const FOCUSABLE_SELECTOR =
  'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared logic for suppressing Tab so the browser can navigate between
 * focusable elements inside an AG Grid cell or header cell.
 *
 * Returns true (suppress) while there is still a next/previous focusable
 * element inside the container; returns false once Tab would leave the
 * container so AG Grid can perform its normal navigation.
 *
 * `containerSelector` is the CSS selector for the cell or header cell
 * (.ag-cell or .ag-header-cell).
 */
const shouldSuppressTabForInnerNavigation = (
  event: KeyboardEvent,
  containerSelector: string
): boolean => {
  if (event.key !== 'Tab') return false;

  const target = event.target as HTMLElement;
  const containerEl = target.closest<HTMLElement>(containerSelector);
  if (!containerEl) return false;

  const focusable = Array.from(
    containerEl.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  );
  if (focusable.length === 0) return false;

  const currentIndex = focusable.indexOf(target);

  if (!event.shiftKey) {
    // currentIndex === -1 means focus is on the container itself, not an inner
    // element — suppress so the browser can Tab to the first inner element.
    return currentIndex < focusable.length - 1;
  }
  return currentIndex > 0;
};

const suppressTabForInnerCellNavigation = (
  params: SuppressKeyboardEventParams
): boolean => {
  if (params.editing) return false;
  return shouldSuppressTabForInnerNavigation(params.event, '.ag-cell');
};

const isEnterOnInnerFocusable = (
  event: KeyboardEvent,
  containerSelector: string
): boolean => {
  if (event.key !== 'Enter') return false;
  const target = event.target as HTMLElement;
  const containerEl = target.closest<HTMLElement>(containerSelector);
  if (!containerEl) return false;
  const focusable = Array.from(
    containerEl.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  );
  return focusable.includes(target);
};

const suppressHeaderKeyboardEventForInnerNavigation = (
  params: SuppressHeaderKeyboardEventParams
): boolean =>
  shouldSuppressTabForInnerNavigation(params.event, '.ag-header-cell') ||
  isEnterOnInnerFocusable(params.event, '.ag-header-cell');

const suppressDeleteAndBackspace = (params: SuppressKeyboardEventParams) => {
  const event = params.event;
  return event.key === 'Delete' || event.key === 'Backspace';
};

const getColumnSpecificSuppressor = (column: ReportColumn) => {
  if (columnOperations.isLucidchartColumn(column)) {
    return suppressDeleteAndBackspace;
  }
  if (
    columnOperations.isDateColumn(column) ||
    columnOperations.isDateRangeColumn(column) ||
    columnOperations.isDescriptionColumn(column) ||
    columnOperations.isTextAreaColumn(column)
  ) {
    return suppressEnterTabEscapeAndArrowKeys;
  } else if (columnOperations.isReferenceColumn(column)) {
    return suppressEnterAndTabForDialogEditors;
  } else if (
    columnOperations.isTypeColumn(column) ||
    columnOperations.isListColumn(column) ||
    columnOperations.isUserColumn(column) ||
    columnOperations.isWorkspaceColumn(column) ||
    columnOperations.isParentColumn(column) ||
    columnOperations.isSelectMultipleListColumn(column) ||
    columnOperations.isTagsColumn(column)
  ) {
    return suppressEnterForComboboxEditors;
  }
  return () => false;
};

const getShouldSuppressKeyBoardEvent = (column: ReportColumn) => {
  const columnSpecific = getColumnSpecificSuppressor(column);
  if (columnOperations.isNameColumn(column)) {
    return (params: SuppressKeyboardEventParams) =>
      columnSpecific(params) || suppressTabForInnerCellNavigation(params);
  }
  return columnSpecific;
};

export const columnOperations = {
  isDateColumn,
  isDateTimeColumn,
  isDateOnlyColumn,
  isCheckBoxColumn,
  isDateRangeColumn,
  isDateTimeRangeColumn,
  isDateOnlyRangeColumn,
  isUserColumn,
  isTextAreaColumn,
  isNumberColumn,
  isNameColumn,
  isParentColumn,
  isProvenanceColumn,
  isColumnSortable,
  isReferenceColumn,
  getAgGridReferenceColumnDirection,
  isURLColumn,
  isEmailColumn,
  isListColumn,
  isSelectMultipleListColumn,
  isTypeColumn,
  isWorkspaceColumn,
  isTagsColumn,
  isArdoqOID,
  isFileColumn,
  isLucidchartColumn,
  getIsCellEditable,
  isComponentKeyColumn,
  isIdColumn,
  isDescriptionColumn,
  isCreatedAtColumn,
  isLastUpdatedDateColumn,
  getDefaultSort,
  getColumnHeaderLabel,
  isColumnVisibleByDefault,
  getDefaultColumnDefinitions,
  sortColumnsToDefaultOrder,
  getFieldOrderByName,
  columnIdIsForOutgoingReference,
  canColumnUseFillHandle,
  shouldSuppressCellTooltip,
  getShouldSuppressKeyBoardEvent,
  suppressHeaderKeyboardEventForInnerNavigation,
  toLeafColumnDefs,
  hasDisplayedColumnInGroup,
  getColumnId,
  getBackendFieldKey,
  isRequestableColumnDef,
  findColumnDefByColumnId,
  getColumnIdsBackedByField,
  toSorting,
  canEditIncomingReferenceColumn,
};
